import { format, isToday, isTomorrow } from "date-fns";

import { ListRecurrence, ListType } from "@/enum/list.enum";

/**
 * How a list's schedule is described in the overview and the list detail nav (PREC-3908).
 *
 * Both labels used to be hardcoded to a daily cadence: the enrichment toggle's tooltip read
 * "Runs on a daily basis" for every recurring list, and the processing chip derived its next run
 * as `lastRunAt + 1 day`. A list switched to weekly or monthly was therefore told it runs daily
 * and that it runs tomorrow — the report behind this ticket.
 */

/** The adverb for a recurrence, e.g. MONTHLY → "monthly". */
const RECURRENCE_ADVERB: Record<ListRecurrence, string> = {
	[ListRecurrence.DAILY]: "daily",
	[ListRecurrence.WEEKLY]: "weekly",
	[ListRecurrence.MONTHLY]: "monthly",
};

/**
 * The enrichment toggle's hover text.
 *
 * A list with no recurrence stored is daily — that is the backend default
 * (`ensureCronEventForList` falls back to DAILY when `list.recurrence` is unset), so the copy
 * must not claim some other cadence for it.
 */
export const listRunFrequencyLabel = (
	type: ListType | null | undefined,
	recurrence: ListRecurrence | null | undefined
): string => {
	if (type === ListType.ONCE) {
		return "Runs once";
	}

	return `Runs on a ${RECURRENCE_ADVERB[recurrence ?? ListRecurrence.DAILY]} basis`;
};

/**
 * The idle processing chip's "when does this run next" text.
 *
 * `nextRunAt` is the scheduler's own `event.scheduledFor`. It is legitimately absent — a list
 * whose event has not been stamped yet, or one that has just been activated — and in that case
 * the honest answer is that it is scheduled, without inventing a date.
 */
export const listNextRunLabel = (nextRunAt: string | Date | null | undefined): string => {
	if (!nextRunAt) {
		return "Scheduled";
	}

	const next = new Date(nextRunAt);

	if (Number.isNaN(next.getTime())) {
		return "Scheduled";
	}

	if (isToday(next)) {
		return "Scheduled for today";
	}

	if (isTomorrow(next)) {
		return "Scheduled for tomorrow";
	}

	return `Scheduled for ${format(next, "dd.MM.yyyy")}`;
};

/**
 * The per-list run-email toggle's label (PREC-3908).
 *
 * Names the cadence the mail would actually arrive at, which is the list's own run frequency —
 * one mail per completed run. A one-time list runs once, so there is no adverb to offer.
 */
export const runEmailToggleLabel = (
	type: ListType | null | undefined,
	recurrence: ListRecurrence | null | undefined
): string => {
	if (type === ListType.ONCE) {
		return "Send me a notification by email when this list has run";
	}

	return `Send me a notification by email ${RECURRENCE_ADVERB[recurrence ?? ListRecurrence.DAILY]}`;
};
